import { useState, useRef, useEffect } from "react";
import { useApp } from "@/lib/AppContext";

const WHEEL_COLORS = [
  "#1E5FA8", "#2B8FD4", "#F5A623", "#27AE60", "#E74C3C", "#8E44AD", "#16A085"
];

export default function SpinWheel({ offers, onSpin, onSpinComplete, phone }) {
  const { lang } = useApp();
  const canvasRef = useRef(null);
  const spinningRef = useRef(false);
  const rotationRef = useRef(0);
  const animFrameRef = useRef(null);
  const displayOffersRef = useRef([]);
  const [spinning, setSpinning] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | spinning | processing | error
  const [errorMsg, setErrorMsg] = useState("");

  const displayOffers = offers.length > 0 ? offers : [];
  // Keep ref in sync so animation closure always has latest list
  displayOffersRef.current = displayOffers;

  // Draw wheel at a given rotation (pure function, no state)
  const drawWheel = (currentRotation) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const size = canvas.width;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 8;
    const currentOffers = displayOffersRef.current;
    const numSegments = currentOffers.length;
    const arc = (2 * Math.PI) / numSegments;

    ctx.clearRect(0, 0, size, size);

    currentOffers.forEach((offer, i) => {
      const startAngle = currentRotation + i * arc - Math.PI / 2;
      const endAngle = startAngle + arc;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = WHEEL_COLORS[i % WHEEL_COLORS.length];
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(startAngle + arc / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold ${size < 300 ? 10 : 12}px Cairo, sans-serif`;
      ctx.shadowColor = "rgba(0,0,0,0.4)";
      ctx.shadowBlur = 3;
      const text = offer.title || "";
      const maxLen = 12;
      const displayText = text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
      ctx.fillText(displayText, radius - 15, 5);
      ctx.restore();
    });

    // Center circle
    const centerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 35);
    centerGrad.addColorStop(0, "#ffffff");
    centerGrad.addColorStop(0.5, "#e8f4fd");
    centerGrad.addColorStop(1, "#1E5FA8");
    ctx.beginPath();
    ctx.arc(cx, cy, 35, 0, 2 * Math.PI);
    ctx.fillStyle = centerGrad;
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.font = "bold 20px Cairo, sans-serif";
    ctx.textAlign = "center";
    ctx.fillStyle = "#1E5FA8";
    ctx.fillText("🌙", cx, cy + 7);
  };

  // Initial draw + redraw when offers change (only when not spinning)
  useEffect(() => {
    if (!spinningRef.current) {
      drawWheel(rotationRef.current);
    }
  }, [displayOffers.length]);

  // Draw once on mount
  useEffect(() => {
    drawWheel(0);
  }, []);

  const handleSpin = async () => {
    if (spinningRef.current) return;

    const currentOffers = displayOffersRef.current;
    if (currentOffers.length === 0) {
      alert(lang === "ar" ? "عذراً، لا توجد عروض متوفرة حالياً" : "Sorry, no offers available at the moment");
      return;
    }

    spinningRef.current = true;
    setSpinning(true);
    setStatus("spinning");
    setErrorMsg("");

    const numSegments = currentOffers.length;
    const arc = (2 * Math.PI) / numSegments;

    // اختر قطاعاً عشوائياً فوراً وابدأ الأنيميشن بدون انتظار
    const randomIdx = Math.floor(Math.random() * numSegments);
    const startRotation = rotationRef.current;
    const targetAngle = -(randomIdx * arc + arc / 2);
    const extraSpins = 5 * 2 * Math.PI;
    const endRotation = startRotation + extraSpins + (targetAngle - (startRotation % (2 * Math.PI)));

    const duration = 4500;
    const startTime = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 4);

    // استدعاء قاعدة البيانات بالتوازي مع الأنيميشن
    const spinPromise = onSpin(randomIdx);

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentRot = startRotation + (endRotation - startRotation) * easeOut(progress);

      drawWheel(currentRot);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        rotationRef.current = endRotation;
        // بعد توقف العجلة: ننتظر نتيجة قاعدة البيانات مع حالة "معالجة"
        setStatus("processing");
        const timeoutP = new Promise((resolve) => setTimeout(() => resolve("__timeout__"), 20000));
        Promise.race([spinPromise, timeoutP])
          .then((value) => {
            spinningRef.current = false;
            setSpinning(false);
            if (value === "__timeout__") {
              setStatus("error");
              setErrorMsg(lang === "ar" ? "انتهت مهلة الانتظار. تحقق من اتصالك بالإنترنت ثم أعد المحاولة." : "Timed out. Check your connection and try again.");
            } else if (value) {
              setStatus("idle");
              setTimeout(() => onSpinComplete(value), 500);
            } else {
              setStatus("error");
              setErrorMsg(lang === "ar" ? "تعذّر إتمام اللّف. قد تكون استخدمت لفّتك بالفعل، حاول مرة أخرى لاحقاً." : "Could not complete the spin. You may have already used it. Try again later.");
            }
          })
          .catch(() => {
            spinningRef.current = false;
            setSpinning(false);
            setStatus("error");
            setErrorMsg(lang === "ar" ? "حدث خطأ غير متوقع. حاول مرة أخرى." : "An unexpected error occurred. Try again.");
          });
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col items-center gap-6">
      {offers.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="text-6xl mb-4">🎰</div>
          <h2 className="text-2xl font-black text-foreground mb-2">
            {lang === "ar" ? "لا توجد عروض حالياً" : "No Offers Available"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {lang === "ar" 
              ? "عذراً، لا توجد عروض متوفرة في الوقت الحالي. يرجى المحاولة لاحقاً." 
              : "Sorry, no offers are available at the moment. Please try again later."}
          </p>
        </div>
      ) : (
        <>
      <div className="text-center">
        <h2 className="text-2xl font-black text-primary mb-1">🎰 العب واربح!</h2>
        <p className="text-muted-foreground text-sm">اضغط على زر لف العجلة وانتظر مفاجأتك</p>
      </div>

      <div className="relative flex items-center justify-center">
        {/* Pointer */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20 flex flex-col items-center">
          <div className="w-0 h-0 border-l-[14px] border-r-[14px] border-b-[28px] border-l-transparent border-r-transparent border-b-red-500 drop-shadow-lg"></div>
        </div>

        <div className="absolute inset-0 rounded-full pulse-glow pointer-events-none" style={{ margin: "-8px" }}></div>

        <canvas
          ref={canvasRef}
          width={320}
          height={320}
          className="rounded-full shadow-2xl"
          style={{ maxWidth: "320px", maxHeight: "320px" }}
        />
      </div>
      </>
      )}

      <button
        onClick={handleSpin}
        disabled={spinning || status === "processing"}
        className="w-full max-w-xs py-4 px-8 bg-gradient-to-r from-primary to-accent text-white text-xl font-black rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
      >
        {spinning || status === "processing" ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            {status === "processing"
              ? (lang === "ar" ? "جاري معالجة النتيجة..." : "Processing result...")
              : (lang === "ar" ? "جاري اللف..." : "Spinning...")}
          </span>
        ) : (lang === "ar" ? "🎯 لف العجلة الآن!" : "🎯 Spin Now!")}
      </button>

      {status === "error" && (
        <div className="w-full max-w-sm bg-destructive/10 border border-destructive/30 rounded-2xl p-4 text-center space-y-3">
          <p className="text-sm font-bold text-destructive">{errorMsg}</p>
          <button
            onClick={() => { setStatus("idle"); setErrorMsg(""); }}
            className="text-sm font-bold text-primary underline"
          >
            {lang === "ar" ? "إعادة المحاولة" : "Retry"}
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        {lang === "ar" ? "رقم الجوال:" : "Phone:"} {phone}
      </p>
    </div>
  );
}